import noPhotoSmall from '@propertypal/shared/src/resources/fallbacks/no-photo-icon-small.png';
import noPhoto from '@propertypal/shared/src/resources/fallbacks/no-photo-icon.png';
import { Property } from '@propertypal/shared/src/types/property';
import { PropertySearchResult } from '@propertypal/shared/src/types/propertySearch';
import { getMainImage, getThumbImages, getAgentLogos } from '@propertypal/shared/src/utils/property';
import { SubHeading, Text } from '@propertypal/web-ui/src/typography';
import React, { FunctionComponent, useRef } from 'react';
import { useTheme } from 'styled-components';
import useChildRemover from '../hooks/useChildRemover';
import LazyImage from '../image/LazyImage';
import { Box } from '../layout';
import StartEndTime from '../time/StartEndTime';
import EnergyRatingIcon from './EnergyRatingIcon';
import OpenViewingOverlay from './OpenViewingOverlay';
import PropertyBadge from './PropertyBadge';
import PropertyBanner from './PropertyBanner';
import {
  Container,
  Content,
  DescriptionBox,
  FavouriteBox,
  HeroImageBox,
  HeroNoImage,
  ImageContent,
  LinkContainer,
  ThumbImage,
  ThumbImageContainer,
  Wrapper,
} from './PropertyBox.style';
import PropertyBoxFooter from './PropertyBoxFooter';
import PropertyDistance from './PropertyDistance';
import PropertyFavourite from './PropertyFavourite';
import PropertyPrice from './PropertyPrice';
import ShowHomesBox from './ShowHomesBox';

interface Props {
  property: Property;
  disablePremiumProperties?: boolean;
  userSection?: boolean;
  onFavourite?: () => (favourited: boolean, propertyId: number) => Promise<boolean>;
  overlay?: React.ReactElement;
  hideFooter?: boolean;
  locationDetail?: PropertySearchResult['locationDetail'];
  imageLocator?: string;
  thumbImageLocator?: string;
  showAuctionsLabel?: boolean;
  showOpenViewingLabel?: boolean;
  isSingleResult?: boolean;
  loading?: 'lazy' | 'eager';
}

const PropertyBox: FunctionComponent<Props> = (props) => {
  const { property, overlay, hideFooter } = props;
  const isPremium =
    !props.disablePremiumProperties &&
    props.property.premiumListing &&
    !props.userSection &&
    !props.showOpenViewingLabel;
  const useWideDisplay = isPremium || props.isSingleResult;
  const theme = useTheme();
  const [heroImage, heroHash] = getMainImage(property, props.imageLocator);
  const thumbImages = getThumbImages(property, props.thumbImageLocator);
  const accountLogos = getAgentLogos(property, 'STANDARD');
  const showAuctionLabel = !props.userSection && !isPremium && props.showAuctionsLabel && property.auctionTime;
  const showOpenViewingLabel = !props.userSection && !isPremium && props.showOpenViewingLabel && property.openViewing;
  const showOpenViewingOverlay = !showOpenViewingLabel && property.openViewing && !property.openViewing.past;
  const descriptionBox = useRef<HTMLDivElement>(null);

  useChildRemover(descriptionBox.current);

  return (
    <Container className="pp-property-box" $useWideDisplay={useWideDisplay} $userSection={!!props?.userSection}>
      {showAuctionLabel && property.listingTime?.type === 'auction' && property.listingTime.timeText && (
        <StartEndTime text={property.listingTime.timeText} />
      )}

      {showOpenViewingLabel && property.openViewing?.text && <StartEndTime text={property.openViewing.text} />}

      {!!props.property.distance && (
        <PropertyDistance distance={props.property.distance} locationDetail={props.locationDetail} />
      )}

      <Wrapper
        $userSection={!!props?.userSection}
        $useWideDisplay={isPremium}
        $premierColor={props.property.account.logo?.premierBackgroundColour}
      >
        {overlay}
        <LinkContainer
          href={property.path}
          prefetch={false}
          $useWideDisplay={useWideDisplay}
          $userSection={props?.userSection}
        >
          <Content $useWideDisplay={useWideDisplay} $userSection={props?.userSection}>
            <ImageContent $useWideDisplay={useWideDisplay} $userSection={props?.userSection}>
              <HeroImageBox $useWideDisplay={useWideDisplay && isPremium}>
                {!heroImage && (
                  <HeroNoImage src={props.userSection ? noPhotoSmall.src : noPhoto.src} alt="No photo available" />
                )}

                {!!heroImage && (
                  <LazyImage
                    src={heroImage}
                    blurHash={heroHash}
                    width={880}
                    height={645}
                    alt={`${property.displayAddress} photo`}
                    loading={props.loading}
                  />
                )}

                {props.onFavourite && property.type === 'property' && (
                  <FavouriteBox>
                    <PropertyFavourite
                      favourited={property.shortlisted}
                      // @ts-ignore
                      onFavourite={(favourited) => props.onFavourite()(favourited, property.id)}
                      // unique key needed in infinite grid, once item above is removed from list
                      // it would inherit its favourited state value in PropertyFavourite
                      key={property.id}
                    />
                  </FavouriteBox>
                )}

                {!props.disablePremiumProperties && <PropertyBanner property={property} />}

                {/* Prevent auction badge from rendering when there is also an open viewing on property */}
                {/* Auction time is also disabled if the auction label is displayed at the top of the box instead */}
                {!showOpenViewingOverlay && <PropertyBadge property={property} showAuctionTime={!showAuctionLabel} />}

                {showOpenViewingOverlay && property.openViewing && (
                  <OpenViewingOverlay
                    openViewing={property.openViewing}
                    themeName="dark"
                    containerStyle={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: 0,
                    }}
                  />
                )}
              </HeroImageBox>

              {!props.disablePremiumProperties && isPremium && (
                <ThumbImageContainer $useWideDisplay={useWideDisplay}>
                  {thumbImages &&
                    thumbImages.map((thumbImage, index) => (
                      <ThumbImage key={thumbImage.url} $useWideDisplay={useWideDisplay}>
                        <LazyImage
                          src={thumbImage.url}
                          blurHash={thumbImage.blurHash}
                          alt={`${property.displayAddress} photo ${index + 2}`}
                          aria-hidden="true"
                          width={340}
                          height={255}
                          loading={props.loading}
                        />
                      </ThumbImage>
                    ))}
                </ThumbImageContainer>
              )}
            </ImageContent>

            <DescriptionBox ref={descriptionBox}>
              <div>
                <SubHeading fontSize={20} mb={12} fontWeight="600">
                  {property.displayAddress.replace(/\s+/g, ' ')}
                </SubHeading>

                <PropertyPrice
                  containerStyle={{ marginBottom: 20 }}
                  price={property.price}
                  alternativeCurrencyPrice={property.alternativeCurrencyPrice}
                  fontSize={14}
                  status={property.status}
                  comingSoonText={property.comingSoonText}
                />

                <Text fontSize={14} color={theme.textMid} mb={10}>
                  {props.property.briefText}
                </Text>

                <EnergyRatingIcon property={property} hideEpcText />

                {isPremium && (
                  <Box maxWidth={600}>
                    <SubHeading mt={30} fontSize={16}>
                      DESCRIPTION
                    </SubHeading>

                    <Text data-testid="propertyDescription" fontSize={14}>
                      {property.description}
                    </Text>
                  </Box>
                )}
              </div>

              <div>
                <ShowHomesBox property={property} fontSize={14} />

                {!isPremium && !hideFooter && (
                  <PropertyBoxFooter key="1" property={property} premium={isPremium} logos={accountLogos} />
                )}
              </div>
            </DescriptionBox>
          </Content>

          {isPremium && !hideFooter && (
            <PropertyBoxFooter key="2" property={property} premium={isPremium} logos={accountLogos} />
          )}
        </LinkContainer>
      </Wrapper>
    </Container>
  );
};

export default React.memo(PropertyBox);
